import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

interface CanvasBottomPanelVisibilityContextValue {
  anyBottomPanelVisible: boolean
  setBottomPanelVisible: (panelId: string, visible: boolean) => void
}

const CanvasBottomPanelVisibilityContext =
  createContext<CanvasBottomPanelVisibilityContextValue>({
    anyBottomPanelVisible: false,
    setBottomPanelVisible: () => {},
  })

export function CanvasBottomPanelVisibilityProvider({
  children,
}: {
  children: ReactNode
}) {
  const [visiblePanelIds, setVisiblePanelIds] = useState<Set<string>>(() => new Set())

  const setBottomPanelVisible = useCallback((panelId: string, visible: boolean) => {
    if (!panelId) return

    setVisiblePanelIds((previous) => {
      const alreadyVisible = previous.has(panelId)
      if (alreadyVisible === visible) return previous

      const next = new Set(previous)
      if (visible) {
        next.add(panelId)
      } else {
        next.delete(panelId)
      }
      return next
    })
  }, [])

  const value = useMemo(
    () => ({
      anyBottomPanelVisible: visiblePanelIds.size > 0,
      setBottomPanelVisible,
    }),
    [setBottomPanelVisible, visiblePanelIds],
  )

  return (
    <CanvasBottomPanelVisibilityContext.Provider value={value}>
      {children}
    </CanvasBottomPanelVisibilityContext.Provider>
  )
}

export function useCanvasBottomPanelVisibility(): CanvasBottomPanelVisibilityContextValue {
  return useContext(CanvasBottomPanelVisibilityContext)
}

export function useRegisterCanvasBottomPanelVisibility(
  panelId: string,
  visible: boolean,
): void {
  const { setBottomPanelVisible } = useCanvasBottomPanelVisibility()

  useEffect(() => {
    if (!panelId) return

    setBottomPanelVisible(panelId, visible)
    return () => setBottomPanelVisible(panelId, false)
  }, [panelId, setBottomPanelVisible, visible])
}
